import { useCallback, useEffect, useRef, useState } from "react";
import { callAPI } from "../../helper/apiUtils";
import { buildGetChatUrl } from "../../helper/chatMessageCache";
import { decryptMessage } from "../../utils/encryptDecryptFunction";
import { isSystemRow } from "../../utils/chatSelection";

// In-chat search over the whole history (mobile Pro "Search in this chat", web edition).
//
// GET /building/searchChatsInGroup is unusable (its SQL always fails and it only does substring
// matching), so the chat builds its own index: when the search opens, the same
// GET /group/:id/getChat the conversation uses is paged through in the background with a larger
// limit until chatEnd. Per message the index keeps its folded searchable text, createdAt, sender
// and position (which page it lives on at the conversation's own page size). Results come from
// what is indexed so far and grow while pages arrive.
//
// The raw rows stay too (position 0 = newest message when indexing ran), so a jump to a result
// builds its part of the thread from memory instead of paging getChat up to it.
//
// Matching: case-, accent- and umlaut-insensitive (ä/ae, ö/oe, ü/ue, ß/ss are equal). Every term
// of the query must match (AND). Tier 1 = every term matches at the start of a word, tier 2 =
// some term only matches inside a word. Within a tier: newest first.

export const MIN_QUERY_LENGTH = 2;
const INDEX_LIMIT = 100;
// Two folded forms, so "Müller" is found by "müller", "mueller" and "muller":
//   "ae"     ä → ae, ö → oe, ü → ue, ß → ss, other accents stripped
//   "strip"  every accent stripped (ä → a …), ß → ss
const FOLD_AE = { "ä": "ae", "ö": "oe", "ü": "ue", "ß": "ss" };
const FOLD_STRIP = { "ß": "ss" };
const MODES = ["ae", "strip"];
const WORD_CHAR = /[\p{L}\p{N}]/u;
const COMBINING = /[̀-ͯ]/g;

/** Folded (search) form of a text plus, per folded char, the index of the source char. */
export function foldWithMap(text, mode = "ae") {
  const table = mode === "strip" ? FOLD_STRIP : FOLD_AE;
  const src = String(text || "");
  let out = "";
  const map = [];
  for (let i = 0; i < src.length; i += 1) {
    const lower = src[i].toLowerCase();
    let rep = table[lower];
    if (rep === undefined) rep = lower.normalize("NFD").replace(COMBINING, "");
    for (let k = 0; k < rep.length; k += 1) {
      out += rep[k];
      map.push(i);
    }
  }
  return { text: out, map };
}

const foldOne = (text, mode) => foldWithMap(text, mode).text;

/** The indexed form of a text: both folded forms, one per line. */
export const foldForSearch = (text) => MODES.map((mode) => foldOne(text, mode)).join("\n");

/** The terms of a query (lower case, not folded yet); [] while it is shorter than MIN_QUERY_LENGTH. */
export function parseSearchQuery(query) {
  const raw = String(query || "").trim();
  if (raw.length < MIN_QUERY_LENGTH) return [];
  return raw
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

const variantsOf = (term) => {
  const out = [];
  MODES.forEach((mode) => {
    const v = foldOne(term, mode);
    if (v && !out.includes(v)) out.push(v);
  });
  return out;
};

const isWordStart = (folded, at) => at === 0 || !WORD_CHAR.test(folded[at - 1]);

/** 1 = every term at a word start, 2 = every term somewhere, 0 = no match. `folded` = foldForSearch(text). */
export function matchTier(folded, terms) {
  if (!folded || !terms || !terms.length) return 0;
  let allStart = true;
  for (let t = 0; t < terms.length; t += 1) {
    let found = false;
    let start = false;
    variantsOf(terms[t]).forEach((v) => {
      let at = folded.indexOf(v);
      while (at >= 0 && !start) {
        found = true;
        if (isWordStart(folded, at)) start = true;
        at = folded.indexOf(v, at + 1);
      }
    });
    if (!found) return 0;
    if (!start) allStart = false;
  }
  return allStart ? 1 : 2;
}

/**
 * Ranges [start, end) in `text` (source coordinates) where a term matches; only word starts
 * when `wordStartOnly`. Sorted and merged.
 */
export function findMatchRanges(text, terms, wordStartOnly = false) {
  if (!text || !terms || !terms.length) return [];
  const ranges = [];
  MODES.forEach((mode) => {
    const { text: folded, map } = foldWithMap(text, mode);
    terms.forEach((term) => {
      const v = foldOne(term, mode);
      if (!v) return;
      let at = folded.indexOf(v);
      while (at >= 0) {
        if (!wordStartOnly || isWordStart(folded, at)) {
          ranges.push([map[at], map[at + v.length - 1] + 1]);
        }
        at = folded.indexOf(v, at + 1);
      }
    });
  });
  ranges.sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const merged = [];
  ranges.forEach((r) => {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  });
  return merged;
}

const CIPHER = "U2FsdGVkX1";
const plainOf = (value, secretKey) => {
  if (typeof value !== "string" || !value) return "";
  let text = value;
  if (text.startsWith(CIPHER)) {
    if (!secretKey) return "";
    try {
      text = decryptMessage(text, secretKey) || "";
    } catch (_) {
      return "";
    }
    if (text.startsWith(CIPHER)) return "";
  }
  return text
    .replace(/@\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ");
};

/** Everything of a message that search should find (displayed text, original, transcript, file names). */
export function searchableTextOf(m, secretKey) {
  if (!m || m.isDeleted || isSystemRow(m)) return "";
  const type = String(m.messageType || "").toLowerCase();
  if (type === "map" || type === "contact" || type === "poll") return "";
  const parts = [];
  const push = (v) => {
    const t = plainOf(v, secretKey).trim();
    if (t && !parts.includes(t)) parts.push(t);
  };
  push(m.message);
  push(m.originalMessage);
  push(m.translatedMessage);
  if (type === "audio") {
    push(m.transcribedAudio);
    push(m.originalTranscribedAudio);
  }
  const names = Array.isArray(m.attachedFileName) ? m.attachedFileName : [m.attachedFileName];
  names.forEach((n) => push(n));
  if (Array.isArray(m.documents)) m.documents.forEach((d) => d && push(d.attachedFileName));
  return parts.join("\n");
}

const timeOf = (v) => {
  const t = v ? new Date(v).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

/**
 * The search index of one chat.
 *   chatId     the chat (the index is dropped when it changes)
 *   enabled    starts indexing (once per chat) when true
 *   params     getChat params besides page / limit: { userId, translationLang, building, viewByOwner }
 *   secretKey  the chat's key (getChat's groupInfo.secretKey wins)
 *   pageSize   the conversation's own page size (for the page a result lives on)
 * Returns { version, indexing, rank(query), entryOf(id), positionOf(id), rowsBetween(from, to), indexedCount(),
 *           mergeLoaded(list, key), removeIds(ids), applyEdit(id, message, key) }.
 */
export function useChatSearchIndex({ chatId, enabled, params, secretKey, pageSize = 30 }) {
  const stateRef = useRef({
    chatId: null,
    entries: new Map(),
    rows: [],
    posById: new Map(),
    indexedCount: 0,
    token: 0,
    started: false,
    complete: false,
  });
  const [version, setVersion] = useState(0);
  const [indexing, setIndexing] = useState(false);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const keyRef = useRef(secretKey);
  keyRef.current = secretKey;
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const upsert = useCallback((m, key, pos = {}) => {
    if (!m || m.id == null) return;
    const st = stateRef.current;
    const id = String(m.id);
    const text = searchableTextOf(m, key || keyRef.current);
    if (!text) {
      st.entries.delete(id);
      return;
    }
    const prev = st.entries.get(id);
    let page = prev ? prev.page : null;
    let globalIndex = prev ? prev.globalIndex : null;
    if (pos.globalIndex != null) {
      globalIndex = pos.globalIndex;
      page = Math.floor(pos.globalIndex / pageSize) + 1;
    }
    if (pos.page != null) page = pos.page;
    st.entries.set(id, {
      id,
      folded: foldForSearch(text),
      createdAt: timeOf(m.createdAt) || (prev ? prev.createdAt : 0),
      sender: m.senderName || (prev ? prev.sender : ""),
      globalIndex,
      page,
    });
  }, [pageSize]);

  // A new chat starts a new index.
  useEffect(() => {
    const st = stateRef.current;
    st.token += 1;
    st.chatId = chatId;
    st.entries = new Map();
    st.rows = [];
    st.posById = new Map();
    st.indexedCount = 0;
    st.started = false;
    st.complete = false;
    setIndexing(false);
    bump();
  }, [chatId, bump]);

  // Page through the whole history once the search is used.
  useEffect(() => {
    const st = stateRef.current;
    if (!enabled || !chatId || st.started) return;
    st.started = true;
    const token = st.token;
    setIndexing(true);
    (async () => {
      let page = 1;
      for (let guard = 0; guard < 2000; guard += 1) {
        let data = null;
        try {
          const url = buildGetChatUrl({ ...(paramsRef.current || {}), chatId, page, limit: INDEX_LIMIT });
          const res = await callAPI(url, {}, "GET");
          data = res && res.data;
        } catch (_) {
          data = null;
        }
        if (token !== st.token) return;
        if (!data || !data.status) break;
        const list = Array.isArray(data.chatHistory) ? data.chatHistory : [];
        const key = (data.groupInfo && data.groupInfo.secretKey) || keyRef.current;
        const n = list.length;
        const base = (page - 1) * INDEX_LIMIT;
        // A page comes oldest first; position 0 is the newest message of the chat.
        list.forEach((m, j) => {
          st.rows[base + (n - 1 - j)] = m;
          if (m && m.id != null) st.posById.set(String(m.id), base + (n - 1 - j));
          if (!m || m.id == null) return;
          const known = st.entries.get(String(m.id));
          if (m.isDeleted) {
            st.entries.delete(String(m.id));
            return;
          }
          if (known && known.loaded) {
            known.globalIndex = base + (n - 1 - j);
            return;
          }
          upsert(m, key, { globalIndex: base + (n - 1 - j) });
        });
        st.indexedCount = base + n;
        bump();
        if (data.chatEnd || n === 0) {
          st.complete = true;
          break;
        }
        page += 1;
      }
      if (token === st.token) setIndexing(false);
    })();
  }, [enabled, chatId, bump, upsert]);

  /** Messages of the rendered thread (their displayed text incl. the client translation). */
  const mergeLoaded = useCallback((list, key) => {
    const st = stateRef.current;
    let changed = false;
    (Array.isArray(list) ? list : []).forEach((m) => {
      if (!m || m.id == null) return;
      const id = String(m.id);
      if (m.isDeleted || isSystemRow(m)) {
        if (st.entries.delete(id)) changed = true;
        return;
      }
      const before = st.entries.get(id);
      upsert(m, key, { page: m._page != null ? Number(m._page) || null : undefined });
      const after = st.entries.get(id);
      if (after) after.loaded = true;
      if (!before || !after || before.folded !== after.folded) changed = true;
    });
    if (changed) bump();
  }, [upsert, bump]);

  const removeIds = useCallback((ids) => {
    const st = stateRef.current;
    let changed = false;
    (Array.isArray(ids) ? ids : [ids]).forEach((id) => {
      if (id != null && st.entries.delete(String(id))) changed = true;
    });
    if (changed) bump();
  }, [bump]);

  const applyEdit = useCallback((id, message, key) => {
    const st = stateRef.current;
    const entry = id != null ? st.entries.get(String(id)) : null;
    if (!entry) return;
    const text = plainOf(message, key || keyRef.current).trim();
    if (!text) return;
    entry.folded = foldForSearch(text);
    bump();
  }, [bump]);

  const entryOf = useCallback((id) => (id == null ? null : stateRef.current.entries.get(String(id)) || null), []);

  /** Position of any indexed message (0 = newest when indexing ran), or null. */
  const positionOf = useCallback((id) => {
    if (id == null) return null;
    const st = stateRef.current;
    const at = st.posById.get(String(id));
    if (at != null) return at;
    const e = st.entries.get(String(id));
    return e && e.globalIndex != null ? e.globalIndex : null;
  }, []);

  /**
   * Raw rows at positions [from, to) as fresh copies (newest first), cut at what is indexed so far.
   * `end` is true when the slice reaches the oldest message of the chat.
   */
  const rowsBetween = useCallback((from, to) => {
    const st = stateRef.current;
    const start = Math.max(0, from);
    const stop = Math.min(to, st.indexedCount);
    const rows = [];
    for (let i = start; i < stop; i += 1) {
      if (st.rows[i]) rows.push({ ...st.rows[i] });
    }
    return { rows, from: start, to: Math.max(start, stop), end: st.complete && stop >= st.indexedCount };
  }, []);

  const indexedCount = useCallback(() => stateRef.current.indexedCount, []);

  /** Ranked results: [{ id, tier, createdAt, page }]. */
  const rank = useCallback((query) => {
    const terms = parseSearchQuery(query);
    if (!terms.length) return [];
    const out = [];
    stateRef.current.entries.forEach((e) => {
      const tier = matchTier(e.folded, terms);
      if (tier) out.push({ id: e.id, tier, createdAt: e.createdAt, page: e.page });
    });
    out.sort((a, b) => a.tier - b.tier || b.createdAt - a.createdAt);
    return out;
  }, []);

  return { version, indexing, rank, entryOf, positionOf, rowsBetween, indexedCount, mergeLoaded, removeIds, applyEdit };
}
